import type { ChatConnectionState } from './chat-connection-types';
import type {
	AvailabilityRecord,
	AvailabilityStatus,
} from './ChatQueryService';

/**
 * The caller's OWN availability, shared by every control that shows or
 * changes it (the user-menu Status row, the chat status pill on /chat and
 * on the Profile page). One copy in the chat store means a change made in
 * one control shows up in the others immediately (PD-15101).
 *
 * The server returns two statuses and they answer different questions:
 *   - `intentStatus` is what the user CHOSE (null = never chose). Both
 *     controls display this, because it is the thing the user controls.
 *   - `effectiveStatus` is what teammates, routing and the storefront see:
 *     the choice combined with live chat presence. "Available" only counts
 *     while a chat connection is heartbeating; without one it reads as Away.
 */
export interface MyAvailabilityModel {
	userId: number;
	intentStatus: AvailabilityStatus | null;
	effectiveStatus: AvailabilityStatus;
	canReceiveCustomerChats: boolean;
	/**
	 * Epoch ms when the request that produced `effectiveStatus` was sent.
	 * The request START time, not the response time, because the server
	 * read presence somewhere in between and we must not credit it with
	 * a heartbeat that landed after it looked.
	 */
	observedAt: number;
}

export interface DisplayedAvailabilityModel {
	/** The status to label the control with: the user's choice, or the
	 *  system-derived status for a user who has never chosen one. */
	status: AvailabilityStatus | null;
	/** True when the user chose Available but is not actually reachable
	 *  because chat is not connected, so others see them as Away. */
	notConnected: boolean;
}

/**
 * How long after the chat connection comes up before a server read of the
 * caller's effective status can be believed. On every page load the old
 * connection's Last Will clears the presence key, and the new connection's
 * first heartbeat takes a moment to reach Redis. A read inside that window
 * reports Away for a user who is about to be Available, and trusting it
 * would flash "Not connected" on every reload.
 */
export const PRESENCE_SETTLE_MS = 5_000;

/**
 * When a trusted read says an Available user has no presence while chat is
 * connected, check once more after this long. The usual cause is returning
 * from idle (the heartbeat pauses after 5 idle minutes and resumes on the
 * next 60s tick), which clears itself within a minute.
 */
export const PRESENCE_RECHECK_MS = 65_000;

export function toMyAvailabilityModel(
	record: AvailabilityRecord,
	observedAt: number,
): MyAvailabilityModel {
	return {
		userId: record.userId,
		intentStatus: record.intentStatus,
		effectiveStatus: record.status,
		canReceiveCustomerChats: record.canReceiveCustomerChats,
		observedAt,
	};
}

/**
 * Apply a status the user just chose, before the server confirms it (an
 * optimistic click, or the availability broadcast for our own user from
 * another tab or a supervisor override).
 *
 * The effective status assumes presence, because the user is looking at
 * the app right now. Assuming the opposite would flash "Not connected"
 * for the length of the round trip every time someone picks Available.
 * The next server read replaces the assumption with the real answer.
 *
 * Re-applying the status that is already chosen is a no-op. That keeps
 * the broadcast echo of our own write (which can land after the POST
 * response) from overwriting the real effective status the POST returned.
 */
export function withChosenStatus(
	current: MyAvailabilityModel,
	status: AvailabilityStatus,
): MyAvailabilityModel {
	if (current.intentStatus === status) {
		return current;
	}
	return { ...current, intentStatus: status, effectiveStatus: status };
}

/**
 * Whether a server read can be believed about presence: chat has been
 * connected for at least `settleMs` when the read was sent.
 */
export function isObservationSettled(
	observedAt: number,
	connectionState: ChatConnectionState,
	connectedAt: number | null,
	settleMs: number = PRESENCE_SETTLE_MS,
): boolean {
	return (
		connectionState === 'connected' &&
		connectedAt != null &&
		observedAt >= connectedAt + settleMs
	);
}

/**
 * What the status controls should show.
 *
 * The label is always the user's CHOICE (falling back to the system-derived
 * status only for a user who has never chosen). The "Not connected" hint is
 * raised only when the choice is Available and we actually know the user is
 * not reachable:
 *   - chat is reconnecting or has given up: the Last Will cleared presence
 *     the moment the socket dropped, so others already see Away; or
 *   - a server read sent after chat had settled says the effective status
 *     is not Available.
 * While chat is still starting up (or never starts because the user has no
 * chat access) nothing is known, so the hint stays quiet.
 */
export function resolveDisplayedAvailability(input: {
	availability: MyAvailabilityModel | null;
	connectionState: ChatConnectionState;
	connectedAt: number | null;
	settleMs?: number;
}): DisplayedAvailabilityModel {
	const { availability, connectionState, connectedAt, settleMs } = input;
	if (availability == null) {
		return { status: null, notConnected: false };
	}
	const status = availability.intentStatus ?? availability.effectiveStatus;
	if (availability.intentStatus !== 'available') {
		return { status, notConnected: false };
	}
	if (connectionState === 'reconnecting' || connectionState === 'failed') {
		return { status, notConnected: true };
	}
	const settled = isObservationSettled(
		availability.observedAt,
		connectionState,
		connectedAt,
		settleMs,
	);
	return {
		status,
		notConnected: settled && availability.effectiveStatus !== 'available',
	};
}
